import React, { useState, useEffect, useRef } from "react";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  SimpleGrid,
  Image,
} from "@chakra-ui/react";
import { MdDelete, MdEdit, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import CreatePromotion from "./createPromotion";
import { useTranslation } from "react-i18next";
// Import react-select and rename it to avoid conflict with Chakra UI's Select
import ReactSelect from "react-select";

export const PromotionList = () => {
  const { t } = useTranslation();
  const [promotions, setPromotions] = useState([]);
  const [filteredPromotions, setFilteredPromotions] = useState([]);
  const [editablePromotion, setEditablePromotion] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreatePromotionModal, setShowCreatePromotionModal] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const imageInputRef = useRef(null); // Reference for the image input
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "xl" });
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const pageId = 19;
  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  // Define promotion type options
  const promotionTypeOptions = [
    { value: "percentage_discount", label: t("Percentage Discount") },
    { value: "fixed_discount", label: t("Fixed Discount") },
    { value: "free_delivery", label: t("Free Delivery") },
    { value: "special_offer", label: t("Special Offer") },
    { value: "bonus_tokens", label: t("Bonus Tokens") },
  ];
  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: "No permissions found",
          description: "You don't have the required access.",
          status: "error",
          duration: 3000,
          isClosable: true,
position: "top-right",

        });
        return;
      }

      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: "Error loading permissions",
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
position: "top-right",      });
    }
  }, [toast]);
  
  // Fetch all promotions on component mount
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/promotions?page_id=${pageId}`
        );
        if (!response) {
          throw new Error("Failed to fetch promotions");
        }
        // Adjust based on your API response structure
        const data = response.data.data || response.data;
        setPromotions(data);
        setFilteredPromotions(data);
        toast({
          title: "Promotions fetched successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
position: "top-right",

        });
      } catch (error) {
        toast({
          title: "An error occurred.",
          description: error.message || "Failed to fetch promotions.",
          status: "error",
          duration: 5000,
          isClosable: true,
position: "top-right",

        });
      }
    };

    fetchPromotions();
  }, [toast, pageId]);

  // Fetch available services for the multi-select
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/services?page_id=${pageId}`
        );
        if (!response) {
          throw new Error("Failed to fetch services");
        }
        // Adjust based on your API response structure
        const data = response.data.data || response.data;
        setAvailableServices(data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServices();
  }, [pageId]);

  const handleDeletePromotion = async (promotionId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/promotions/promotion/delete?page_id=${pageId}`,
        "DELETE",
        { id: promotionId }
      );
      if (!response) {
        throw new Error("Failed to delete promotion");
      }
      setPromotions((prev) => prev.filter((p) => p.id !== promotionId));
      setFilteredPromotions((prev) => prev.filter((p) => p.id !== promotionId));
      toast({
        title: "Promotion deleted successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to delete promotion.",
        status: "error",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    }
  };

  const handleEditPromotion = (promotion) => {
    setEditablePromotion(promotion);
  };
  const handleSavePromotion = async () => {
    try {
      const data = {
        id: editablePromotion.id,
        promotion_name: editablePromotion.promotion_name,
        promotion_description: editablePromotion.promotion_description,
        type: editablePromotion.type,
        applicable_services: editablePromotion.applicable_services,
        start_date: editablePromotion.start_date,
        end_date: editablePromotion.end_date,
      };

      // Only include image field if promotion type is "special_offer"
      if (editablePromotion.type === "special_offer" && editablePromotion.image) {
        data.image = editablePromotion.image;
      }
      
      setIsSubmitting(true);
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/promotions/promotion/update?page_id=${pageId}`,
        "PATCH",
        data
      );
      if (!response) {
        throw new Error("Failed to update promotion");
      }
      setPromotions((prev) =>
        prev.map((p) => (p.id === editablePromotion.id ? editablePromotion : p))
      );      setFilteredPromotions((prev) =>
        prev.map((p) => (p.id === editablePromotion.id ? editablePromotion : p))
      );
      
      // Clear the file input if it exists
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
      
      setEditablePromotion(null);
      toast({
        title: "Promotion updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message || "Failed to update promotion.",
        status: "error",
        duration: 5000,
        isClosable: true,
position: "top-right",

      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    const filtered = promotions.filter((promotion) =>
      promotion.promotion_name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredPromotions(filtered);
  };

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex
        px={{ base: "10px", md: "25px" }}
        justify="space-between"
        mb="20px"
        align="center"
        flexWrap="wrap"
      >
        <Flex align="center">
          <Text
            color="gray.800"
            fontSize={{ base: "18px", md: "22px" }}
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: "10px", md: "0" }}
          >
            {t("Promotion List")}
          </Text>
          <Button
            onClick={() => setShowCreatePromotionModal(true)}
            mb={1}
            isDisabled={!permissions.canInsert}
            ml={4}
            color="white"
            colorScheme="cyan"
            borderRadius="full"
            boxShadow="sm"
            _hover={{ bg: "green.600", transform: "scale(1.05)" }}
            _active={{ bg: "green.700", transform: "scale(0.95)" }}
            transition="all 0.2s"
          >
            <MdAdd />
          </Button>
        </Flex>
        <Input
          placeholder={t("Search promotion")}
          value={searchQuery}
          onChange={handleSearch}
          size="sm"
          w={{ base: "100%", md: "200px" }}
        />
      </Flex>

      {isMobile ? (
        // MOBILE VIEW - Render as Cards
        <Box overflowY="auto" maxH="70vh" px="10px">
          <SimpleGrid columns={{ base: 1 }} spacing={4} px="10px">
            {filteredPromotions.map((promotion) => (
              <Card key={promotion.id} p="4" borderRadius="md" shadow="md">
                <Box mb="3">
                  <Text fontSize="lg" fontWeight="bold">
                    {promotion.promotion_name}
                  </Text>                <Text fontSize="sm" color="gray.500">
                    {promotion.promotion_description}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm">
                    <b>{t("Valid Until")}: </b> {promotion.end_date}
                  </Text>
                  {promotion.type === "special_offer" && promotion.image && (
                    <Box mt={2} mb={2}>
                      <Image 
                        src={promotion.image} 
                        alt={promotion.promotion_name}
                        boxSize="60px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                    </Box>
                  )}
                  <Flex mt="2">
                    <Button
                      colorScheme="cyan"
                      color={"white"}
                      size="xs"
                      isDisabled={!permissions.canEdit}
                      mr="2"
                      onClick={() => handleEditPromotion(promotion)}
                      leftIcon={<MdEdit />}
                    >
                      {t("Edit")}
                    </Button>
                    <Button
                      isDisabled={!permissions.canDelete}
                      colorScheme="red"
                      size="xs"
                      onClick={() => handleDeletePromotion(promotion.id)}
                      leftIcon={<MdDelete />}
                    >
                      {t("Delete")}
                    </Button>
                  </Flex>
                </Box>
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      ) : (
        // DESKTOP VIEW - Render as Table
        <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
          <Table variant="simple">
            <Thead>              <Tr bg='rgb(69, 50, 240)'  textColor="white">
                <Th textColor="white">{t("Index")}</Th>
                <Th textColor="white">{t("Title")}</Th>
                <Th textColor="white">{t("Description")}</Th>
                <Th textColor="white">{t("Valid Until")}</Th>
                <Th textColor="white">{t("Type")}</Th>
                <Th textColor="white">{t("Image")}</Th>
                <Th textColor="white">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredPromotions.map((promotion, index) => (
                <Tr key={promotion.id}>
                  <Td>{index + 1}</Td>                  <Td>{promotion.promotion_name}</Td>
                  <Td>{promotion.promotion_description}</Td>
                  <Td>{promotion.end_date}</Td>
                  <Td>{promotion.type}</Td>
                  <Td>
                    {promotion.type === "special_offer" && promotion.image ? (
                      <Image 
                        src={promotion.image} 
                        alt={promotion.promotion_name}
                        boxSize="50px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        colorScheme="cyan"
                        size="sm"
                        mr="2"
                        color={"white"}
                        onClick={() => handleEditPromotion(promotion)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        size="sm"
                        onClick={() => handleDeletePromotion(promotion.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {editablePromotion && (
        <Modal isOpen={true} onClose={() => setEditablePromotion(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
                {t("Promotion Info")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {/* Promotion Name */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">
                    {t("Title")}
                  </Text>
                  <Input
                    value={editablePromotion.promotion_name || ""}
                    onChange={(e) =>
                      setEditablePromotion({
                        ...editablePromotion,
                        promotion_name: e.target.value,
                      })
                    }
                  />
                </Box>
                {/* Promotion Description */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">
                    {t("Description")}
                  </Text>
                  <Input
                    value={editablePromotion.promotion_description || ""}
                    onChange={(e) =>
                      setEditablePromotion({
                        ...editablePromotion,
                        promotion_description: e.target.value,
                      })
                    }
                  />
                </Box>
                {/* Promotion Type using react-select */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">
                    {t("Type")}
                  </Text>
                  <ReactSelect
                    value={
                      promotionTypeOptions.find(
                        (option) => option.value === editablePromotion.type
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      setEditablePromotion({ ...editablePromotion, type: selectedOption.value })
                    }
                    options={promotionTypeOptions}
                    placeholder={t("Select Type")}
                  />
                </Box>
                
                {/* Image Upload - Only show for special offer type */}
                {editablePromotion.type === "special_offer" && (
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb="2">
                      {t("Image")}
                    </Text>
                    <Flex direction="column" align="center">
                      {editablePromotion.image && (
                        <Box mb={3}>
                          <Image 
                            src={editablePromotion.image} 
                            alt={editablePromotion.promotion_name}
                            boxSize="100px"
                            objectFit="cover"
                            borderRadius="md"
                          />
                        </Box>
                      )}                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setEditablePromotion({
                                ...editablePromotion,
                                image: reader.result,
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        ref={imageInputRef}
                      />
                    </Flex>
                  </Box>
                )}
                {/* Applicable Services using react-select multi-select */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">
                    {t("Applicable Services")}
                  </Text>
                  <ReactSelect
                    isMulti
                    value={Array.isArray(editablePromotion.applicable_services) ? 
                      editablePromotion.applicable_services.map((service) => ({
                        value: service,
                        label: service,
                      })) : []
                    }
                    onChange={(selectedOptions) =>
                      setEditablePromotion({
                        ...editablePromotion,
                        applicable_services: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                      })
                    }
                    options={(Array.isArray(availableServices) ? availableServices : []).map((service) => ({
                      value: service.name,
                      label: service.name,
                    }))}
                    placeholder={t("Select Services")}
                  />

                </Box>
                {/* Start Date */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">
                    {t("Start Date")}
                  </Text>
                  <Input
                    type="date"
                    value={editablePromotion.start_date || ""}
                    onChange={(e) =>
                      setEditablePromotion({ ...editablePromotion, start_date: e.target.value })
                    }
                  />
                </Box>
                {/* Valid Until */}
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">
                    {t("Valid Until")}
                  </Text>
                  <Input
                    type="date"
                    value={editablePromotion.end_date || ""}
                    onChange={(e) =>
                      setEditablePromotion({ ...editablePromotion, end_date: e.target.value })
                    }
                  />
                </Box>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="cyan"
                onClick={handleSavePromotion}
                color="white"
                isLoading={isSubmitting}
                borderRadius="full"
                boxShadow="md"
              >
                {t("Save")}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => setEditablePromotion(null)}
                borderRadius="full"
                boxShadow="md"
              >
                {t("Cancel")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal
        isOpen={showCreatePromotionModal}
        onClose={() => setShowCreatePromotionModal(false)}
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent borderRadius="lg" boxShadow="xl" bg={useColorModeValue("white", "gray.800")}>
          <ModalHeader>
            <Text color={textColor} fontSize="2xl" fontWeight="700">
              {t("Create Promotion")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreatePromotion />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default PromotionList;
